import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronRight, TicketCheck, TicketPlus } from 'lucide-react';
import { useCrashGroups, useCrashes } from '@/services/queries';
import type { CrashGroup, CrashGroupQuery } from '@/types';
import { StatusPill } from '@/shared/ui/status-pill';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ErrorState } from '@/components/common';
import { formatDateTime, formatRelative } from '@/shared/lib/format';
import { ROUTES } from '@/constants';
import { RaiseIssueDialog } from '../issues/RaiseIssueDialog';

const ANY = 'any';
const COMPONENTS = ['nudent', 'nucad'] as const;
const WINDOWS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
] as const;
const REPORTS_SHOWN = 10;

const componentName = (value: string) => (value === 'nucad' ? 'NμCAD' : 'NμDent');

/**
 * Crash triage, grouped by signature as the design asks.
 *
 * One row per normalised summary - paths, ids and numbers taken out on the
 * server - so a fault that hit forty machines is one row with "40 machines"
 * rather than forty rows. Opening a row shows its newest reports; "Create
 * issue" raises a pre-filled Issues record for it.
 *
 * Still read-only on the crashes themselves: they are corrected in the product,
 * not annotated here.
 */
export function CrashTable() {
  const [component, setComponent] = useState<string>(ANY);
  const [days, setDays] = useState<number>(7);
  const [open, setOpen] = useState<string | null>(null);

  const query: CrashGroupQuery = {
    days,
    ...(component !== ANY ? { appComponent: component as CrashGroupQuery['appComponent'] } : {}),
  };
  const groups = useCrashGroups(query);
  const total = (groups.data ?? []).reduce((sum, group) => sum + group.count, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={component}
          onValueChange={(value) => {
            setComponent(value);
            setOpen(null);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Filter by component">
            <SelectValue placeholder="Component" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All components</SelectItem>
            {COMPONENTS.map((value) => (
              <SelectItem key={value} value={value}>
                {componentName(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={String(days)}
          onValueChange={(value) => {
            setDays(Number(value));
            setOpen(null);
          }}
        >
          <SelectTrigger className="w-40" aria-label="Time window">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOWS.map((option) => (
              <SelectItem key={option.days} value={String(option.days)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {groups.data && (
          <p className="ml-auto text-[12.5px] text-muted-foreground">
            {groups.data.length} {groups.data.length === 1 ? 'group' : 'groups'} ·{' '}
            {total.toLocaleString()} {total === 1 ? 'report' : 'reports'}
          </p>
        )}
      </div>

      {groups.isError && (
        <ErrorState
          title="Could not load crashes"
          description={groups.error.message}
          action={
            <Button
              variant="outline"
              onClick={() => {
                void groups.refetch();
              }}
            >
              Try again
            </Button>
          }
        />
      )}

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Signature</TableHead>
              <TableHead className="hidden w-28 sm:table-cell">Component</TableHead>
              <TableHead className="w-24 text-right">Reports</TableHead>
              <TableHead className="hidden w-24 text-right md:table-cell">Machines</TableHead>
              <TableHead className="hidden w-36 lg:table-cell">Versions</TableHead>
              <TableHead className="w-36">Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.isPending &&
              Array.from({ length: 6 }, (_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))}

            {groups.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  No crashes reported in this window. That is the desired state.
                </TableCell>
              </TableRow>
            )}

            {groups.data?.map((group) => {
              const expanded = open === group.signature;
              return (
                <Fragment key={group.signature}>
                  <TableRow
                    className="cursor-pointer"
                    aria-expanded={expanded}
                    onClick={() => {
                      setOpen(expanded ? null : group.signature);
                    }}
                  >
                    <TableCell className="text-muted-foreground">
                      {expanded ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </TableCell>
                    <TableCell className="max-w-md">
                      <span className="font-mono text-[12px] wrap-break-word">{group.title}</span>
                      {group.severity && <StatusPill status={group.severity} className="ml-2" />}
                      {group.issue && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                          title={group.issue.subject}
                        >
                          <TicketCheck className="size-3" />
                          Issue {group.issue.status === 'resolved' ? 'resolved' : 'open'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground sm:table-cell">
                      {componentName(group.appComponent)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {group.count.toLocaleString()}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums text-muted-foreground md:table-cell">
                      {group.devices.toLocaleString()}
                    </TableCell>
                    <TableCell className="hidden text-muted-foreground tabular-nums lg:table-cell">
                      {group.versions.slice(0, 2).join(', ') || '—'}
                      {group.versions.length > 2 && ` +${String(group.versions.length - 2)}`}
                    </TableCell>
                    <TableCell
                      className="text-muted-foreground"
                      title={formatDateTime(group.lastSeenAt)}
                    >
                      {formatRelative(group.lastSeenAt)}
                    </TableCell>
                  </TableRow>
                  {expanded && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell />
                      <TableCell colSpan={6} className="pb-5">
                        <GroupReports group={group} days={days} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableCard>
    </div>
  );
}

/** One group, opened: when it started, its newest reports, and raising an issue. */
function GroupReports({ group, days }: { group: CrashGroup; days: number }) {
  const reports = useCrashes({ signature: group.signature, limit: REPORTS_SHOWN, offset: 0 });

  const description = [
    `Crash group ${group.signature} (${componentName(group.appComponent)}).`,
    `${String(group.count)} reports from ${String(group.devices)} machines in the last ${String(days)} days.`,
    `Versions: ${group.versions.join(', ') || 'unknown'}.`,
    `First seen ${formatDateTime(group.firstSeenAt)}, last seen ${formatDateTime(group.lastSeenAt)}.`,
    '',
    `Signature: ${group.title}`,
  ].join('\n');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-muted-foreground">
        <span>
          First seen {formatDateTime(group.firstSeenAt)} · signature{' '}
          <span className="font-mono">{group.signature}</span>
        </span>
        {group.issue && group.issue.status !== 'resolved' ? (
          <Button size="sm" variant="outline" className="ml-auto" asChild>
            <Link to={ROUTES.issues} title={group.issue.subject}>
              <TicketCheck className="size-4" />
              Open issue
            </Link>
          </Button>
        ) : (
          <RaiseIssueDialog
            preset={{
              portal: 'desktop',
              subject: `Crash: ${group.title}`.slice(0, 200),
              description,
              severity: group.severity === 'fatal' ? 'critical' : 'high',
              crashSignature: group.signature,
            }}
            trigger={
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <TicketPlus className="size-4" />
                {group.issue ? 'Raise again' : 'Create issue'}
              </Button>
            }
          />
        )}
      </div>

      {reports.isPending && <Skeleton className="h-16 w-full" />}
      {reports.data && (
        <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
          {reports.data.data.map((report) => (
            <li key={report.id} className="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2 text-[12.5px]">
              <span className="min-w-0 flex-1 wrap-break-word">
                {report.summary ?? 'No summary'}
              </span>
              <span className="text-muted-foreground tabular-nums">{report.appVersion ?? '—'}</span>
              <span className="font-mono text-[11.5px] text-muted-foreground">
                {report.deviceId ? report.deviceId.slice(0, 8) : 'no device'}
              </span>
              <span className="text-muted-foreground tabular-nums">
                {formatDateTime(report.ingestedAt)}
              </span>
            </li>
          ))}
          {reports.data.meta.total > reports.data.data.length && (
            <li className="px-3 py-2 text-[12px] text-muted-foreground">
              Newest {reports.data.data.length} of {reports.data.meta.total.toLocaleString()}{' '}
              reports.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
